import { useState, useEffect, useRef } from 'react'
import { GameState } from '../store/types'
import { apiHuntPlaceList, apiActionStart, apiActionCancel, apiMonsterList, apiHuntDropList } from '../server/ServiceCall'
import { HuntTickResult } from '../App'

const REGION_NAME: Record<string, string> = {
  RGN_BUKDO:'북도', RGN_BUKHAI:'북해', RGN_DAEMO:'대막', RGN_DALI:'대리국',
  RGN_GAEBEONG:'개봉', RGN_HAEDONG:'해동', RGN_HANGSAN:'항산', RGN_HEBEI:'하북',
  RGN_HONAM:'호남', RGN_HUASHAN:'화산', RGN_HWANGSAN:'황산', RGN_JANGAN:'장안',
  RGN_JANGBACK:'장백산', RGN_JIANGNAN:'강남', RGN_JIANGSU:'강소', RGN_JIRIM:'지림',
  RGN_MINGGU:'광명정', RGN_NAKYANG:'낙양', RGN_NAMMAN:'남만', RGN_NANDO:'남도',
  RGN_SANDO:'산동', RGN_SHAOLIN:'소림사', RGN_SICHUAN:'사천', RGN_TIBET:'서장',
  RGN_TOEI:'동영', RGN_WUDANG:'무당산', RGN_XIYUE:'서역', RGN_ZHONGYUAN:'중원',
  RGN_CHEONWOECHEON:'천외천',
}

interface HuntPlace {
  placeId:     string
  regionId:    string
  placeName:   string
  minPower:    number
  goldMin:     number
  goldMax:     number
  description: string
}

interface HuntLog {
  turn:         number
  goldDelta:    number
  dropItemId:   string | null
  dropItemName: string | null
  dropQty:      number
  monsterName:  string | null
  hpDamage:     number
  autoHpUsed:   number
}

interface Monster {
  monsterId:   string
  monsterName: string
  grade:       'NORMAL' | 'ELITE' | 'BOSS'
  minPower:    number
  hp:          number
  atk:         number
  def:         number
  goldBonus:   number
  dropMult:    number
  description: string
}

interface DropItem {
  itemId:   string
  itemName: string
  grade:    string
  qtyMin:   number
  qtyMax:   number
  dropRate: number | null
}

const GRADE_COLOR: Record<string, string> = {
  NORMAL: '#a0a0a0',
  ELITE:  '#4090e0',
  BOSS:   '#e04060',
}
const GRADE_LABEL: Record<string, string> = {
  NORMAL: '일반',
  ELITE:  '정예',
  BOSS:   '두목',
}
const ITEM_GRADE_COLOR: Record<string, string> = {
  COMMON:'#a0a0a0', UNCOMMON:'#60c060', RARE:'#4090e0',
  EPIC:'#a060e0', LEGEND:'#e0a030', MYTH:'#e04060',
}
const ITEM_GRADE_KO: Record<string, string> = {
  COMMON:'일반', UNCOMMON:'고급', RARE:'희귀', EPIC:'영웅', LEGEND:'전설', MYTH:'신화',
}

interface Props {
  gs:              GameState
  token:           string
  onRefresh:       () => void
  onForceLogout:   () => void
  lastHuntResult:  HuntTickResult | null
}

export default function TabHunt({ gs, token, onRefresh, onForceLogout, lastHuntResult }: Props) {
  const [places,      setPlaces]      = useState<HuntPlace[]>([])
  const [allPlaces,   setAllPlaces]   = useState<HuntPlace[]>([])
  const [selected,    setSelected]    = useState<HuntPlace | null>(null)
  const [loading,     setLoading]     = useState(false)
  const [busy,        setBusy]        = useState(false)
  const [msg,         setMsg]         = useState('')
  const [msgOk,       setMsgOk]       = useState(false)
  const [huntLogs,    setHuntLogs]    = useState<HuntLog[]>([])
  const [monsters,    setMonsters]    = useState<Monster[]>([])
  const [monLoading,  setMonLoading]  = useState(false)
  const [drops,       setDrops]       = useState<DropItem[]>([])
  const [showDrops,   setShowDrops]   = useState(false)
  const [dropLoading, setDropLoading] = useState(false)
  const logRef = useRef<HTMLDivElement>(null)

  const isHunting   = gs.char.activeAction === 'HUNT'
  const isAnyAction = gs.char.activeAction !== null

  // 새 턴 결과 수신 시 로그에 추가
  useEffect(() => {
    if (!lastHuntResult || lastHuntResult.turnsDone === 0) return
    setHuntLogs(prev => {
      const entry: HuntLog = {
        turn:         lastHuntResult.turn,
        goldDelta:    lastHuntResult.goldDelta,
        dropItemId:   lastHuntResult.dropItemId,
        dropItemName: lastHuntResult.dropItemName,
        dropQty:      lastHuntResult.dropQty,
        monsterName:  lastHuntResult.monsterName,
        hpDamage:   lastHuntResult.hpDamage,
        autoHpUsed: lastHuntResult.autoHpUsed,
      }
      return [entry, ...prev].slice(0, 5)
    })
  }, [lastHuntResult])

  // 사냥 종료 시 로그 초기화
  useEffect(() => {
    if (!isHunting) setHuntLogs([])
  }, [isHunting])

  // 로그 최상단 스크롤
  useEffect(() => {
    logRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
  }, [huntLogs])

  function showMsg(m: string, ok = false) {
    setMsg(m); setMsgOk(ok)
    setTimeout(() => setMsg(''), 3000)
  }

  useEffect(() => {
    if (!gs.char.currentRegion) return
    setLoading(true)
    const toHuntPlace = (r: any): HuntPlace => ({
      placeId:     r.PLACE_ID,
      regionId:    r.REGION_ID,
      placeName:   r.PLACE_NAME,
      minPower:    Number(r.MIN_POWER) || 0,
      goldMin:     Number(r.GOLD_MIN)  || 0,
      goldMax:     Number(r.GOLD_MAX)  || 0,
      description: r.DESCRIPTION ?? '',
    })
    const activeRegion = gs.char.activeRegion
    const needExtra = activeRegion && activeRegion !== gs.char.currentRegion

    const p1 = apiHuntPlaceList(gs.char.currentRegion).then(res =>
      res.resultCode === '00' ? (JSON.parse(res.resultData || '[]') as any[]).map(toHuntPlace) : []
    )
    const p2 = needExtra
      ? apiHuntPlaceList(activeRegion!).then(res =>
          res.resultCode === '00' ? (JSON.parse(res.resultData || '[]') as any[]).map(toHuntPlace) : []
        )
      : Promise.resolve([] as HuntPlace[])

    Promise.all([p1, p2])
      .then(([cur, extra]) => {
        setPlaces(cur)
        setAllPlaces([...cur, ...extra])
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [gs.char.currentRegion, gs.char.activeRegion, token])

  // 장소 선택 시 몬스터 목록 로드
  useEffect(() => {
    setDrops([]); setShowDrops(false)
    if (!selected) { setMonsters([]); return }
    setMonLoading(true)
    apiMonsterList(selected.placeId)
      .then(res => {
        if (res.resultCode === '00' && res.resultData) {
          const DELIM     = '\x18'
          const ROW_DELIM = '\x19'
          const rows = res.resultData.split(ROW_DELIM).filter(r => r)
          const parsed: Monster[] = rows.map(row => {
            const f = row.split(DELIM)
            return {
              monsterId:   f[0] ?? '',
              monsterName: f[1] ?? '',
              grade:       (f[2] ?? 'NORMAL') as Monster['grade'],
              minPower:    Number(f[3]) || 0,
              hp:          Number(f[4]) || 0,
              atk:         Number(f[5]) || 0,
              def:         Number(f[6]) || 0,
              goldBonus:   Number(f[7]) || 0,
              dropMult:    Number(f[8]) || 1,
              description: f[9] ?? '',
            }
          })
          setMonsters(parsed)
        } else {
          setMonsters([])
        }
      })
      .catch(() => setMonsters([]))
      .finally(() => setMonLoading(false))
  }, [selected])

  async function toggleDrops(place: HuntPlace) {
    if (showDrops) { setShowDrops(false); return }
    if (drops.length > 0) { setShowDrops(true); return }
    setDropLoading(true)
    try {
      const res = await apiHuntDropList(token, place.placeId)
      if (res.resultCode === '00') {
        const raw = JSON.parse(res.resultData || '[]') as any[]
        setDrops(raw.map(r => ({
          itemId:   r.ITEM_ID,
          itemName: r.ITEM_NAME,
          grade:    r.GRADE,
          qtyMin:   Number(r.QTY_MIN) || 1,
          qtyMax:   Number(r.QTY_MAX) || 1,
          dropRate: r.DROP_RATE != null ? Number(r.DROP_RATE) : null,
        })))
      }
      setShowDrops(true)
    } catch {}
    finally { setDropLoading(false) }
  }

  async function doStart(place: HuntPlace) {
    setBusy(true)
    try {
      const res = await apiActionStart(token, 'HUNT', place.placeId)
      if (res.resultCode === '97') { onForceLogout(); return }
      showMsg(res.resultMessage, res.resultCode === '00')
      if (res.resultCode === '00') onRefresh()
    } catch { showMsg('서버 오류') }
    finally { setBusy(false) }
  }

  async function doCancel() {
    setBusy(true)
    try {
      const res = await apiActionCancel(token)
      showMsg(res.resultMessage, res.resultCode === '00')
      if (res.resultCode === '00') onRefresh()
    } catch { showMsg('서버 오류') }
    finally { setBusy(false) }
  }

  const myPower = gs.char.totalPower

  return (
    <div className="tab-action">

      {msg && <div className={`inven-msg${msgOk ? ' ok' : ''}`}>{msg}</div>}

      {/* 현재 사냥 상태 */}
      {isHunting && (
        <section className="card-section action-active">
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <div>
              <div className="card-title" style={{ color:'#e0a030' }}>사냥 중</div>
              <div style={{ fontSize:13, color:'#c8b890' }}>
                {allPlaces.find(p => p.placeId === gs.char.activeTarget)?.placeName ?? gs.char.activeTarget}
              </div>
            </div>
            <button className="action-btn drop" disabled={busy} onClick={doCancel}>귀환</button>
          </div>

          {/* 사냥 결과 로그 */}
          <div ref={logRef} style={{ marginTop:10, maxHeight:200, overflowY:'auto', display:'flex', flexDirection:'column', gap:4 }}>
            {huntLogs.length === 0 && (
              <div style={{ color:'#605040', fontSize:12, textAlign:'center', padding:'8px 0' }}>
                다음 턴에 결과가 표시됩니다...
              </div>
            )}
            {huntLogs.map((log, i) => (
              <div key={log.turn} style={{
                padding:'5px 8px', borderRadius:3,
                background: i === 0 ? 'rgba(200,130,10,0.15)' : 'rgba(255,255,255,0.03)',
                borderLeft: i === 0 ? '2px solid #c8820a' : '2px solid #3a2e1a',
                fontSize:12,
              }}>
                {log.monsterName && (
                  <div style={{ fontSize:11, color:'#c0905a', marginBottom:2 }}>
                    ⚔ {log.monsterName} 처치
                  </div>
                )}
                <span style={{ color:'#f0c060' }}>+{log.goldDelta.toLocaleString()}전</span>
                {log.hpDamage > 0 && (
                  <span style={{ color:'#e05050', marginLeft:8 }}>생명력 -{log.hpDamage}</span>
                )}
                {log.dropItemId && (
                  <span style={{ color:'#80d080', marginLeft:8 }}>
                    {log.dropItemName ?? log.dropItemId} ×{log.dropQty}
                  </span>
                )}
                {!log.dropItemId && (
                  <span style={{ color:'#504030', marginLeft:8 }}>아이템 없음</span>
                )}
                {log.autoHpUsed > 0 && (
                  <div style={{ marginTop:2, fontSize:10, color:'#90c060' }}>
                    <span>소모품(체력) 자동사용</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {isAnyAction && !isHunting && (
        <section className="card-section action-busy">
          <div style={{ color:'#c8820a', fontSize:13 }}>
            현재 다른 행동 중 ({gs.char.activeAction === 'TRAIN' ? '수련' : gs.char.activeAction})
          </div>
          <button className="action-btn drop" style={{ marginTop:8 }}
            disabled={busy} onClick={doCancel}>행동 취소</button>
        </section>
      )}

      {/* 사냥 장소 목록 */}
      <section className="card-section">
        <div className="card-title">
          {REGION_NAME[gs.char.currentRegion] ?? gs.char.currentRegion} 사냥 장소
          <span style={{ fontSize:10, color:'#706050', marginLeft:8 }}>
            내 전투력: {myPower.toLocaleString()}
          </span>
        </div>
        {loading ? (
          <div style={{ color:'#605040', textAlign:'center', padding:'20px 0' }}>불러오는 중...</div>
        ) : places.length === 0 ? (
          <div style={{ color:'#605040', textAlign:'center', padding:'20px 0' }}>이 지역에 사냥 장소가 없습니다.</div>
        ) : (
          <div className="hunt-place-list">
            {places.map(place => {
              const canEnter = myPower >= place.minPower
              const isActive = gs.char.activeTarget === place.placeId && isHunting
              return (
                <div key={place.placeId}
                  className={`hunt-place-item${!canEnter ? ' locked' : ''}${selected?.placeId===place.placeId?' selected':''}${isActive?' active-hunt':''}`}
                  onClick={() => setSelected(selected?.placeId===place.placeId ? null : place)}
                >
                  <div className="hunt-place-top">
                    <span className="hunt-place-name">{place.placeName}</span>
                    {!canEnter && <span className="hunt-locked-badge">전투력 부족</span>}
                    {isActive  && <span className="hunt-active-badge">사냥 중</span>}
                  </div>
                  <div className="hunt-place-info">
                    <span>골드 {place.goldMin}~{place.goldMax}</span>
                    {place.minPower > 0 && (
                      <span style={{ color: canEnter ? '#706050' : '#c04040' }}>
                        권장 전투력 {place.minPower.toLocaleString()}
                      </span>
                    )}
                  </div>
                  {place.description && (
                    <div className="hunt-place-desc">{place.description}</div>
                  )}
                  {selected?.placeId === place.placeId && (
                    <div onClick={e => e.stopPropagation()}>
                      {/* 몬스터 목록 */}
                      <div style={{ marginTop:10, marginBottom:6 }}>
                        <div style={{ fontSize:11, color:'#807060', marginBottom:5 }}>출현 몬스터</div>
                        {monLoading ? (
                          <div style={{ color:'#605040', fontSize:11, padding:'4px 0' }}>불러오는 중...</div>
                        ) : monsters.length === 0 ? (
                          <div style={{ color:'#504030', fontSize:11 }}>몬스터 정보 없음</div>
                        ) : (
                          <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                            {monsters.map(m => (
                              <div key={m.monsterId} style={{
                                padding:'5px 8px',
                                borderRadius:3,
                                background:'rgba(0,0,0,0.25)',
                                borderLeft:`3px solid ${GRADE_COLOR[m.grade] ?? '#a0a0a0'}`,
                                fontSize:11,
                              }}>
                                <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:2 }}>
                                  <span style={{ color: GRADE_COLOR[m.grade], fontWeight:'bold', fontSize:10 }}>
                                    [{GRADE_LABEL[m.grade]}]
                                  </span>
                                  <span style={{ color:'#e8d8b0', fontSize:12 }}>{m.monsterName}</span>
                                  {m.goldBonus > 0 && (
                                    <span style={{ color:'#f0c060', marginLeft:'auto', fontSize:10 }}>
                                      +{m.goldBonus}전
                                    </span>
                                  )}
                                </div>
                                <div style={{ color:'#706050', display:'flex', gap:8 }}>
                                  <span>HP {m.hp}</span>
                                  <span>공 {m.atk}</span>
                                  <span>방 {m.def}</span>
                                  {m.dropMult > 1 && (
                                    <span style={{ color:'#80d080' }}>드롭×{m.dropMult}</span>
                                  )}
                                </div>
                                {m.description && (
                                  <div style={{ color:'#504840', marginTop:2, lineHeight:1.4 }}>
                                    {m.description}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      {/* 드롭 아이템 상세보기 */}
                      <div style={{ marginTop:8 }}>
                        <button
                          style={{ background:'none', border:'none', cursor:'pointer', padding:0,
                            fontSize:11, color:'#8090a0', display:'flex', alignItems:'center', gap:3 }}
                          onClick={() => toggleDrops(place)}
                        >
                          {dropLoading ? '불러오는 중...' : `드롭 아이템 ${showDrops ? '▲' : '▼'}`}
                        </button>
                        {showDrops && (
                          <div style={{ marginTop:5, display:'flex', flexDirection:'column', gap:2 }}>
                            {drops.length === 0 ? (
                              <div style={{ fontSize:11, color:'#504030' }}>드롭 아이템 없음</div>
                            ) : drops.map(d => (
                              <div key={d.itemId} style={{
                                display:'flex', alignItems:'center', gap:6,
                                fontSize:11, padding:'2px 4px',
                                borderRadius:2, background:'rgba(0,0,0,0.2)',
                              }}>
                                <span style={{ width:6, height:6, borderRadius:'50%', flexShrink:0,
                                  background: ITEM_GRADE_COLOR[d.grade] ?? '#a0a0a0' }} />
                                <span style={{ color: ITEM_GRADE_COLOR[d.grade] ?? '#c8b890', flex:1 }}>
                                  {d.itemName}
                                </span>
                                <span style={{ color:'#706050', flexShrink:0 }}>
                                  {d.qtyMin === d.qtyMax ? `×${d.qtyMin}` : `×${d.qtyMin}~${d.qtyMax}`}
                                </span>
                                {d.dropRate != null && !['EPIC','LEGEND','MYTH'].includes(d.grade) && (
                                  <span style={{ color:'#a08060', flexShrink:0, minWidth:36, textAlign:'right' }}>
                                    {(d.dropRate * 100).toFixed(1)}%
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {canEnter && !isAnyAction && (
                        <button className="action-btn equip" style={{ marginTop:8, width:'100%' }}
                          disabled={busy} onClick={() => doStart(place)}>
                          이 장소에서 사냥 시작
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </section>

    </div>
  )
}
